import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

// Modal component for legal documents
const LegalModal = ({ isOpen, onClose, activeTab, setActiveTab }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('privacy')}
              className={`text-sm font-medium transition-colors ${
                activeTab === 'privacy' 
                  ? 'text-[hsl(var(--text-primary))]' 
                  : 'text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-secondary))]'
              }`}
            >
              Política de Privacidad
            </button>
            <button
              onClick={() => setActiveTab('terms')}
              className={`text-sm font-medium transition-colors ${
                activeTab === 'terms' 
                  ? 'text-[hsl(var(--text-primary))]' 
                  : 'text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-secondary))]'
              }`}
            >
              Términos y Condiciones
            </button>
          </div>
          <button
            onClick={onClose}
            className="text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'privacy' ? (
            <div className="prose prose-sm max-w-none">
              <h2 className="text-xl font-bold mb-4">Política de Privacidad</h2>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-[hsl(var(--text-secondary))] mb-2"><strong>Responsable del tratamiento</strong></p>
                <ul className="list-disc pl-5 text-[hsl(var(--text-secondary))] space-y-1">
                  <li>Titular: <strong>David Santiago Ponce Velásquez</strong></li>
                  <li>DNI: <strong>41751936J</strong></li>
                  <li>Actividad: Servicios de <strong>consultoría y automatización estratégica</strong></li>
                  <li>Correo electrónico de contacto: <strong>automatizaconcabeza@davidautomatiza.com</strong></li>
                </ul>
              </div>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">1. Información general</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                La presente Política de Privacidad regula el tratamiento de los datos personales recabados a través de formularios, comunicaciones y servicios prestados por David Santiago Ponce Velásquez, de conformidad con el Reglamento (UE) 2016/679 (RGPD) y la normativa española vigente en materia de protección de datos.
              </p>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Al facilitar datos personales, el usuario declara haber leído y comprendido esta política y acepta el tratamiento de sus datos conforme a lo aquí establecido.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">2. Datos personales que se recogen</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-2">
                Los datos que pueden ser tratados incluyen, entre otros:
              </p>
              <ul className="list-disc pl-5 text-[hsl(var(--text-secondary))] mb-4 space-y-1">
                <li>Datos identificativos: nombre y apellidos</li>
                <li>Datos de contacto: correo electrónico y número de teléfono</li>
                <li>Datos profesionales: cargo, empresa, métricas de negocio y respuestas a formularios de diagnóstico</li>
                <li>Cualquier otra información que el usuario facilite voluntariamente</li>
              </ul>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                No se tratan categorías especiales de datos.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">3. Finalidades del tratamiento</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-2">
                Los datos personales se tratan con las siguientes finalidades:
              </p>
              <ul className="list-disc pl-5 text-[hsl(var(--text-secondary))] mb-4 space-y-1">
                <li>Gestionar solicitudes de análisis, diagnósticos y servicios de consultoría</li>
                <li>Elaborar informes personalizados de negocio y automatización</li>
                <li>Mantener comunicaciones profesionales relacionadas con los servicios ofrecidos</li>
                <li>Dar cumplimiento a obligaciones legales y contractuales</li>
              </ul>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Los datos no serán utilizados para fines distintos de los descritos.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">4. Base legal del tratamiento</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-2">
                La base legal para el tratamiento de los datos es:
              </p>
              <ul className="list-disc pl-5 text-[hsl(var(--text-secondary))] mb-4 space-y-1">
                <li>El <strong>consentimiento expreso</strong> del usuario</li>
                <li>El <strong>cumplimiento de obligaciones legales</strong> aplicables</li>
              </ul>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">5. Conservación de los datos</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-2">
                Los datos personales se conservarán:
              </p>
              <ul className="list-disc pl-5 text-[hsl(var(--text-secondary))] mb-4 space-y-1">
                <li>Mientras exista una relación profesional o contractual</li>
                <li>Durante el tiempo necesario para cumplir con las obligaciones legales</li>
                <li>Hasta que el usuario solicite su supresión, cuando sea legalmente posible</li>
              </ul>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">6. Destinatarios de los datos</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Los datos no se cederán a terceros salvo obligación legal o cuando sea necesario para la prestación del servicio (por ejemplo, herramientas tecnológicas utilizadas para la gestión de formularios, CRM o automatización), garantizando en todo caso el cumplimiento del RGPD.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">7. Derechos del usuario</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-2">
                El usuario puede ejercer los siguientes derechos:
              </p>
              <ul className="list-disc pl-5 text-[hsl(var(--text-secondary))] mb-4 space-y-1">
                <li>Acceso a sus datos personales</li>
                <li>Rectificación de datos inexactos</li>
                <li>Supresión (derecho al olvido)</li>
                <li>Limitación del tratamiento</li>
                <li>Oposición al tratamiento</li>
                <li>Portabilidad de los datos</li>
              </ul>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Para ejercer estos derechos, el usuario puede enviar una solicitud a través del correo electrónico indicado, adjuntando copia de un documento identificativo.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">8. Medidas de seguridad</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Se aplican medidas técnicas y organizativas adecuadas para garantizar la confidencialidad, integridad y disponibilidad de los datos personales, evitando accesos no autorizados, pérdida o alteración de la información.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">9. Cambios en la política de privacidad</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Esta política puede actualizarse en función de cambios legales o de actividad. La versión vigente será siempre la publicada en el momento del acceso.
              </p>
            </div>
          ) : (
            <div className="prose prose-sm max-w-none">
              <h2 className="text-xl font-bold mb-4">Términos y Condiciones</h2>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">1. Objeto</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Los presentes Términos y Condiciones regulan el acceso y uso de los servicios de consultoría y automatización estratégica ofrecidos por David Santiago Ponce Velásquez.
              </p>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                El acceso a los formularios, diagnósticos y servicios implica la aceptación plena de estos términos.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">2. Servicios ofrecidos</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-2">
                Los servicios incluyen, entre otros:
              </p>
              <ul className="list-disc pl-5 text-[hsl(var(--text-secondary))] mb-4 space-y-1">
                <li>Análisis y diagnósticos de negocio</li>
                <li>Consultoría estratégica</li>
                <li>Diseño e implementación de automatizaciones</li>
                <li>Recomendaciones personalizadas basadas en la información facilitada por el usuario</li>
              </ul>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Los servicios tienen carácter profesional y estratégico, no garantizando resultados económicos concretos.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">3. Obligaciones del usuario</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-2">
                El usuario se compromete a:
              </p>
              <ul className="list-disc pl-5 text-[hsl(var(--text-secondary))] mb-4 space-y-1">
                <li>Facilitar información veraz y actualizada</li>
                <li>Utilizar los servicios de forma lícita y conforme a la normativa vigente</li>
                <li>No utilizar los contenidos con fines fraudulentos o no autorizados</li>
              </ul>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">4. Responsabilidad</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-2">
                El titular no se hace responsable de:
              </p>
              <ul className="list-disc pl-5 text-[hsl(var(--text-secondary))] mb-4 space-y-1">
                <li>Decisiones empresariales tomadas por el usuario basadas en los análisis o recomendaciones</li>
                <li>Resultados derivados de la implementación de estrategias sugeridas</li>
                <li>Daños derivados del uso indebido de la información proporcionada</li>
              </ul>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">5. Propiedad intelectual</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Todos los contenidos, metodologías, informes y materiales entregados están protegidos por derechos de propiedad intelectual y son titularidad de David Santiago Ponce Velásquez, salvo indicación expresa en contrario.
              </p>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Queda prohibida su reproducción, distribución o cesión sin autorización expresa.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">6. Confidencialidad</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Ambas partes se comprometen a mantener la confidencialidad de la información intercambiada en el marco de la relación profesional.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">7. Modificaciones</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                El titular se reserva el derecho a modificar estos Términos y Condiciones en cualquier momento. Las modificaciones serán aplicables desde su publicación.
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-2">8. Legislación aplicable y jurisdicción</h3>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Estos Términos y Condiciones se rigen por la legislación española.
              </p>
              <p className="text-[hsl(var(--text-secondary))] mb-4">
                Para la resolución de cualquier conflicto, las partes se someten a los Juzgados y Tribunales del domicilio del titular, salvo que la normativa aplicable disponga lo contrario.
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-gray-100">
          <Button
            onClick={onClose}
            className="w-full h-12 text-base font-semibold"
          >
            Volver al formulario
          </Button>
        </div>
      </div>
    </div>
  );
};

const ContactScreen = ({ onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "+34 "
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalTab, setLegalTab] = useState('privacy');

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTermsChange = (checked) => {
    setAcceptedTerms(checked);
    if (checked && errors.terms) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.terms;
        return newErrors;
      });
    }
  };

  const openLegalModal = (tab) => {
    setLegalTab(tab);
    setShowLegalModal(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = "El nombre es obligatorio.";
    }
    if (!formData.email.trim()) {
      newErrors.email = "El email es obligatorio.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Por favor, introduce un email válido.";
    }
    if (!formData.telefono.trim() || formData.telefono.trim() === "+34") {
      newErrors.telefono = "El teléfono es obligatorio.";
    }
    if (!acceptedTerms) {
      newErrors.terms = "Debes aceptar la Política de Privacidad y los Términos y Condiciones para continuar.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit({
        ...formData,
        preferencia: "email" // Default value
      });
    }
  };

  return (
    <>
      <div className="min-h-screen flex items-center justify-center pb-8 sm:pb-12 px-4 slide-up" style={{ paddingTop: '110px' }}>
        <div className="max-w-2xl w-full space-y-6 sm:space-y-8">
          <div className="space-y-3 sm:space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[hsl(var(--text-primary))] leading-tight" style={{ letterSpacing: '-0.02em' }}>
              ¿Dónde te enviamos tu análisis?
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-[hsl(var(--text-secondary))] leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
              Recibirás un análisis personalizado con los puntos de ruptura de tu negocio y las oportunidades reales de automatización con IA.
            </p>
          </div>

          <div className="space-y-4 sm:space-y-5">
            {/* Name */}
            <div className="space-y-2">
              <label className="text-base font-semibold text-[hsl(var(--text-primary))]" style={{ letterSpacing: '-0.01em' }}>
                Nombre
              </label>
              <Input
                value={formData.nombre}
                onChange={(e) => handleChange("nombre", e.target.value)}
                placeholder="Tu nombre completo"
                className="h-12 text-base"
              />
              {errors.nombre && (
                <p className="text-sm text-[hsl(var(--state-error))]">{errors.nombre}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-base font-semibold text-[hsl(var(--text-primary))]" style={{ letterSpacing: '-0.01em' }}>
                Email
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="tu@email.com"
                className="h-12 text-base"
              />
              {errors.email && (
                <p className="text-sm text-[hsl(var(--state-error))]">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label className="text-base font-semibold text-[hsl(var(--text-primary))]" style={{ letterSpacing: '-0.01em' }}>
                Teléfono
              </label>
              <Input
                type="tel"
                value={formData.telefono}
                onChange={(e) => handleChange("telefono", e.target.value)}
                placeholder="600 000 000"
                className="h-12 text-base"
              />
              <p className="text-xs text-[hsl(var(--text-tertiary))]">
                Prefijo por defecto: España (+34). Modifícalo si es necesario.
              </p>
              {errors.telefono && (
                <p className="text-sm text-[hsl(var(--state-error))]">{errors.telefono}</p>
              )}
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className="space-y-2 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={acceptedTerms}
                  onCheckedChange={handleTermsChange}
                  className="mt-0.5"
                />
                <label 
                  htmlFor="terms" 
                  className="text-sm text-[hsl(var(--text-secondary))] leading-relaxed cursor-pointer"
                >
                  He leído y acepto la{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      openLegalModal('privacy');
                    }}
                    className="text-[hsl(var(--accent-primary))] hover:underline font-medium"
                  >
                    Política de Privacidad
                  </button>
                  {' '}y los{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      openLegalModal('terms');
                    }}
                    className="text-[hsl(var(--accent-primary))] hover:underline font-medium"
                  >
                    Términos y Condiciones
                  </button>
                  .
                </label>
              </div>
              {errors.terms && (
                <p className="text-sm text-[hsl(var(--state-error))] ml-7">{errors.terms}</p>
              )}
            </div>
          </div>

          <div className="pt-4">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              size="lg"
              className="w-full h-14 text-base font-semibold"
            >
              {isSubmitting ? "Enviando..." : "Recibir mi análisis"}
            </Button>
          </div>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal
        isOpen={showLegalModal}
        onClose={() => setShowLegalModal(false)}
        activeTab={legalTab}
        setActiveTab={setLegalTab}
      />
    </>
  );
};

export default ContactScreen;
